import { Box, Button, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { templateFormAtom } from '../../state/templateAtoms';

function formatMessage(text) {
  if (!text) return null;

  const elements = [];
  const regex = /(\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  const matches = [...text.matchAll(regex)];

  matches.forEach((match, index) => {
    const matchText = match[0];
    const start = match.index;

    if (start > lastIndex) {
      elements.push(text.slice(lastIndex, start));
    }

    if (matchText.startsWith('*') && matchText.endsWith('*')) {
      elements.push(<strong key={`bold-${index}`}>{matchText.slice(1, -1)}</strong>);
    } else if (matchText.startsWith('_') && matchText.endsWith('_')) {
      elements.push(<em key={`italic-${index}`}>{matchText.slice(1, -1)}</em>);
    }

    lastIndex = start + matchText.length;
  });

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return elements;
}

function getBodyMessage(template) {
  const body = template?.components?.find((obj) => obj?.type === 'BODY');
  if (body?.text && body?.example?.body_text?.[0]?.length) {
    return body.text.replace(/{{(\d+)}}/g, (match, number) => {
      const index = parseInt(number, 10) - 1;
      return body.example.body_text[0][index] || match;
    });
  }
  return body?.text ?? '';
}

export default function MessagePreview() {
  const template = useRecoilValue(templateFormAtom);

  const headerComponent = template?.components?.find((obj) => obj?.type === 'HEADER');
  const headerType = headerComponent?.format;
  const headerText = headerComponent?.text || '';
  const mediaUrl = template?.media?.header || null;
  const footerText = template?.components?.find((obj) => obj?.type === 'FOOTER')?.text || '';
  const buttons =
    template?.components?.find((obj) => obj?.type === 'BUTTONS')?.buttons ?? [];

  const renderHeaderContent = () => {
    if (!headerComponent) return null;

    if (headerType === 'IMAGE' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <img
            src={mediaUrl}
            alt="Header"
            style={{ width: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }}
          />
        </Box>
      );
    }

    if (headerType === 'VIDEO' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <video controls style={{ width: '100%', maxHeight: 200, borderRadius: 8 }}>
            <source src={mediaUrl} type="video/mp4" />
          </video>
        </Box>
      );
    }

    if (headerType === 'DOCUMENT' && mediaUrl) {
      const fileName = mediaUrl.split('/').pop() || 'Header document';
      return (
        <Box sx={{ mb: 1 }}>
          <Typography
            component="a"
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: 'block', color: '#128C7E', textDecoration: 'none', wordBreak: 'break-word' }}
          >
            {fileName}
          </Typography>
        </Box>
      );
    }

    if (headerType === 'TEXT') {
      return (
        <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {headerText}
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        maxWidth: 360,
        mx: 'auto',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, wordBreak: 'break-word' }}>
        {template?.name || 'Template'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: '#128C7E',
          color: '#fff',
          px: 2,
          py: 1,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Template message
        </Typography>
        <Typography variant="caption">Now</Typography>
      </Box>

      <Box
        sx={{
          bgcolor: '#dcf8c6',
          p: 2,
          borderRadius: 1,
          my: 2,
        }}
      >
        {renderHeaderContent()}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', mt: 0.5 }}>
          {formatMessage(getBodyMessage(template)) || 'Body message not available'}
        </Typography>

        {buttons.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {buttons.map((item, index) => (
              <Button
                key={index}
                variant="outlined"
                size="small"
                sx={{
                  color: '#128C7E',
                  borderColor: '#128C7E',
                  textTransform: 'none',
                  fontSize: 12,
                }}
              >
                {item?.text || 'Button'}
              </Button>
            ))}
          </Box>
        ) : null}

        {footerText ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
            {formatMessage(footerText)}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
